import axios from "axios";

const API_BASE_URL = "/api/bills";

/**
 * Bill API Service
 * Handles all HTTP requests related to bill operations
 */
class BillService {
  /**
   * Create a new bill
   */
  async createBill(billData) {
    try {
      const response = await axios.post(API_BASE_URL, billData, {
        headers: { "Content-Type": "application/json" },
      });
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  /**
   * Get all bills
   */
  async getAllBills(filters = {}) {
    try {
      const params = new URLSearchParams();
      if (filters.partyId) params.append("partyId", filters.partyId);
      if (filters.status) params.append("status", filters.status);
      if (filters.billDateFrom) params.append("billDateFrom", filters.billDateFrom);
      if (filters.billDateTo) params.append("billDateTo", filters.billDateTo);

      const queryString = params.toString();
      const url = queryString ? `${API_BASE_URL}?${queryString}` : API_BASE_URL;
      const response = await axios.get(url);
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  /**
   * Get next available invoice number (unique, not already used)
   */
  async getNextInvoiceNumber() {
    try {
      const response = await axios.get(`${API_BASE_URL}/next-invoice-number`);
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  /**
   * Get single bill by ID
   */
  async getBillById(billId) {
    try {
      const response = await axios.get(`${API_BASE_URL}/${billId}`);
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  /**
   * Get bill PDF as blob (for viewing - use API to avoid static route issues)
   */
  async getBillPdfBlob(billId) {
    try {
      const response = await axios.get(`${API_BASE_URL}/${billId}/pdf`, {
        responseType: "blob",
        withCredentials: true,
        headers: { Accept: "application/pdf" },
      });
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  /**
   * Update bill
   */
  async updateBill(billId, updateData) {
    try {
      const response = await axios.put(`${API_BASE_URL}/${billId}`, updateData, {
        headers: { "Content-Type": "application/json" },
      });
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  /**
   * Delete bill
   */
  async deleteBill(billId) {
    try {
      const response = await axios.delete(`${API_BASE_URL}/${billId}`);
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  /**
   * Handle API errors
   */
  handleError(error) {
    if (error.response) {
      return {
        message: error.response.data?.message || "An error occurred",
        status: error.response.status,
      };
    } else if (error.request) {
      return {
        message: "No response from server. Please check your connection.",
        status: 0,
      };
    } else {
      return {
        message: error.message || "An unexpected error occurred",
        status: 0,
      };
    }
  }
}

const billServiceInstance = new BillService();
export default billServiceInstance;
